import { z } from "zod";

/**
 * Контракт входа мобильного приложения — замена `POST /api/auth/quick`
 * старого SSO (`docs/LEGACY-CUTOVER-TASKS.md` §1).
 *
 * ЧТО ПЕРЕНЕСЕНО И ЧТО НЕТ. Форма ответа — прежняя, вплоть до имён
 * `old_portal_cookie` / `old_portal_token` в snake_case: приложение разбирает
 * именно её. Способ доказательства владения адресом — новый: пароля в IEC ID
 * нет и не будет (Д1), поэтому вместо `{email, password}` тут два шага и код
 * из письма, тот же, что на вебе.
 *
 * Маршруты сырые (Fastify + TypeBox), поэтому здесь Zod-зеркало формы:
 * контракт живёт в одном месте, а `routes/mobile/mobile.schemas.ts` обязан ему
 * соответствовать. Расходятся они молча, поэтому сверять — при каждой правке.
 */

/**
 * Grant-тип клиента, которому разрешён этот вход.
 *
 * Строка одна на реестр (`oauth_clients.grant_types`), проверку маршрута и сид.
 * Держится ЗДЕСЬ, потому что `shared/db/seeds/**` не имеет права импортировать
 * `modules/**` (`rules/module-boundaries.md` §1), а литерал в двух местах
 * разъезжается на первой же правке (грабли §27).
 */
export const MOBILE_EMAIL_CODE_GRANT = "email_code";

/** Шаг 1: попросить код на почту. */
export const MobileCodeRequestSchema = z.object({
	email: z.email().max(320),
	/** Язык письма. Не задан — берём из профиля, затем из `Accept-Language`. */
	locale: z.string().min(2).max(8).optional(),
});

export const MobileCodeResponseSchema = z.object({
	success: z.literal(true),
	/** Через сколько секунд можно просить следующий код. */
	resend_after: z.number().int().nonnegative(),
});

/** Шаг 2: обменять код на пару токенов. */
export const MobileQuickAuthRequestSchema = z.object({
	email: z.email().max(320),
	code: z.string().min(4).max(12),
	/** Клиент мобильного приложения. Проверяется по реестру `oauth_clients`. */
	client_id: z.string().min(1).max(128),
	/** Отпечаток устройства — та же роль, что у веба: узнавание, не доверие. */
	device_id: z.string().max(128).optional(),
});

export const MobileQuickAuthResponseSchema = z.object({
	success: z.literal(true),
	access_token: z.string(),
	token_type: z.literal("Bearer"),
	/**
	 * Секунды. У старого SSO здесь был захардкоженный год
	 * (`PassportTokenService.php:35`); у нас час, и приложение ОБЯЗАНО
	 * пользоваться `refresh_token`.
	 */
	expires_in: z.number().int().positive(),
	refresh_token: z.string(),
	/**
	 * Пара ключей к анкете человека в старом портале.
	 *
	 * По умолчанию `null`: держим их server-to-server (Портал берёт их
	 * `GET /api/v1/legacy/old-portal-session/:sub`), потому что этой парой
	 * чужую анкету можно не только читать, но и править. Заполняются, только
	 * если владелец включил `MOBILE_EXPOSE_OLD_PORTAL_SESSION` — то есть
	 * подтвердил, что приложение ходит в `IECIS.WEB.API` само.
	 */
	old_portal_cookie: z.string().nullable(),
	old_portal_token: z.string().nullable(),
});

/** Отказ в форме старого SSO: `{success:false, error}` плюс код для разбора. */
export const MobileErrorResponseSchema = z.object({
	success: z.literal(false),
	error: z.string(),
	code: z.string(),
	/** Только для `429`: через сколько секунд повторять. */
	resend_after: z.number().int().nonnegative().optional(),
});

export type MobileCodeRequest = z.infer<typeof MobileCodeRequestSchema>;
export type MobileQuickAuthRequest = z.infer<typeof MobileQuickAuthRequestSchema>;
export type MobileQuickAuthResponse = z.infer<typeof MobileQuickAuthResponseSchema>;
